import type { ReactNode } from "react";
import React, { createContext, useContext, useState, useCallback } from "react";

import axios, { type AxiosResponse } from "axios";

import type {
  FacilityListSerializer,
  ProviderDirectoryFacilitiesList200Response,
} from "~/src/types";

export interface FacilityDataProps {
  results: FacilityListSerializer[];
  loading?: boolean;
  error?: string | null;
  count?: number;
}

interface FacilityContextType {
  facilityData: FacilityDataProps;
  fetchFacilities: (searchParams?: string) => void;
  facilityResults: FacilityListSerializer[];
  setFacilityData: (data: FacilityDataProps) => void;
  setFacilityResults: (results: FacilityListSerializer[]) => void;
}

const FacilityContext = createContext<FacilityContextType | undefined>(
  undefined,
);

export const FacilityProvider = ({ children }: { children: ReactNode }) => {
  const [facilityData, setFacilityData] = useState<FacilityDataProps>({
    results: [],
    loading: true,
    error: null,
  });

  const fetchFacilities = useCallback((searchParams: string = "") => {
    setFacilityData((prev) => {
      return {
        ...prev,
        loading: true,
      };
    });

    axios
      .get(
        `provider_directory/facilities${searchParams.startsWith("?") ? "" : "?"}${searchParams}`,
      )
      .then(
        (res: AxiosResponse<ProviderDirectoryFacilitiesList200Response>) => {
          setFacilityData({
            results: res.data.result?.results || [],
            count: res.data.result?.count || 0,
            loading: false,
            error: null,
          });
        },
      )
      .catch((err) => {
        console.error(err);
        setFacilityData({
          results: [],
          loading: false,
          error: "Something went wrong loading facilities",
        });
      });
  }, []);

  const setFacilityDataFunc = useCallback((data: FacilityDataProps) => {
    setFacilityData((prev) => {
      return {
        ...prev,
        ...data,
      };
    });
  }, []);

  const setFacilityResults = useCallback(
    (results: FacilityListSerializer[]) => {
      setFacilityData((prev) => {
        return {
          ...prev,
          results,
          loading: false,
        };
      });
    },
    [],
  );

  const contextValue: FacilityContextType = {
    facilityData,
    fetchFacilities,
    facilityResults: facilityData.results,
    setFacilityData: setFacilityDataFunc,
    setFacilityResults,
  };

  return (
    <FacilityContext.Provider value={contextValue}>
      {children}
    </FacilityContext.Provider>
  );
};

export const useFacilities = () => {
  const context = useContext(FacilityContext);

  if (context === undefined) {
    throw new Error("useFacilities must be used within a FacilityContext");
  }

  return context;
};
